/**
 * @fileoverview Componente responsável pela navegação inferior em contextos responsivos.
 */

import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Drawer } from '@material-ui/core';
import {
  MessageCircle,
  UserRound,
  ChartNoAxesCombined,
  MoreHorizontal,
  X,
  Pen,
  Tags,
  Link,
  Workflow,
  UsersRound,
  Settings,
  LayoutDashboard,
  LogOut,
  Sun,
  Moon,
  Globe,
  Volume2,
  VolumeX,
  Volume1,
  ChevronRight,
  GitBranch,
  SquareKanban,
  MessagesSquare,
  Briefcase,
  Building2,
  FileText,
  Newspaper,
} from 'lucide-react';
import { Badge, Avatar, Slider } from '@material-ui/core';
import { i18n, changeLanguage } from '../../translate/i18n';
import { AuthContext } from '../../context/Auth/AuthContext';
import { SocketContext } from '../../context/Socket/SocketContext';
import { WhatsAppsContext } from '../../context/WhatsApp/WhatsAppsContext';
import { Can } from '../Can';
import usePlans from '../../hooks/usePlans';
import api from '../../services/api';

const languages = [
  { code: 'pt', flag: '🇧🇷', label: 'PT' },
  { code: 'en', flag: '🇺🇸', label: 'EN' },
  { code: 'es', flag: '🇪🇸', label: 'ES' }
];

const useStyles = makeStyles((theme) => ({
  // Bottom Navigation Bar
  bottomNav: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    height: 64,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around',
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(26, 26, 26, 0.95)'
      : 'rgba(255, 255, 255, 0.95)',
    borderTop: theme.palette.type === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.08)'
      : '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: theme.palette.type === 'dark'
      ? '0 -4px 20px rgba(0, 0, 0, 0.3)'
      : '0 -4px 20px rgba(0, 0, 0, 0.08)',
    zIndex: 1300,
    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
  },

  navItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 16px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    borderRadius: 12,
    minWidth: 64,
    '&:active': {
      transform: 'scale(0.92)',
    },
  },

  navItemActive: {
    '& $navIcon': {
      color: '#1e88e5',
      transform: 'scale(1.1)',
    },
    '& $navLabel': {
      color: '#1e88e5',
      fontWeight: 600,
    },
  },

  navIcon: {
    color: theme.palette.type === 'dark' ? '#88898D' : '#9f9f9f',
    marginBottom: 4,
    transition: 'all 0.2s ease',
  },

  navLabel: {
    fontSize: 11,
    fontWeight: 500,
    color: theme.palette.type === 'dark' ? '#6D6F73' : '#666',
    transition: 'all 0.2s ease',
  },

  // More Drawer
  drawerPaper: {
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(26, 26, 26, 0.98)'
      : 'rgba(255, 255, 255, 0.98)',
    maxHeight: '85vh',
    paddingBottom: 'env(safe-area-inset-bottom, 16px)',
  },

  drawerHandle: {
    width: 40,
    height: 4,
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(255, 255, 255, 0.2)'
      : 'rgba(0, 0, 0, 0.15)',
    borderRadius: 2,
    margin: '12px auto 8px',
  },

  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 20px 16px',
    borderBottom: theme.palette.type === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.06)'
      : '1px solid rgba(0, 0, 0, 0.06)',
  },

  drawerTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: theme.palette.type === 'dark' ? '#f1f5f9' : '#1a1a2e',
  },

  closeButton: {
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
    cursor: 'pointer',
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.04)',
    color: theme.palette.type === 'dark' ? '#94a3b8' : '#64748b',
    transition: 'all 0.15s ease',
    '&:active': {
      transform: 'scale(0.95)',
    },
  },

  drawerContent: {
    padding: '8px 12px',
    overflowY: 'auto',
  },

  menuSection: {
    marginBottom: 8,
  },

  sectionTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.type === 'dark' ? '#64748b' : '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    padding: '12px 12px 8px',
  },

  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '14px 16px',
    borderRadius: 14,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    '&:active': {
      backgroundColor: theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.06)',
      transform: 'scale(0.98)',
    },
  },

  menuItemActive: {
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(30, 136, 229, 0.15)'
      : 'rgba(30, 136, 229, 0.1)',
    '& $menuItemIcon': {
      color: '#1e88e5',
    },
    '& $menuItemLabel': {
      color: '#1e88e5',
      fontWeight: 600,
    },
  },

  menuItemIcon: {
    color: theme.palette.type === 'dark' ? '#88898D' : '#9f9f9f',
    transition: 'color 0.15s ease',
  },

  menuItemLabel: {
    flex: 1,
    fontSize: 15,
    fontWeight: 500,
    color: theme.palette.type === 'dark' ? '#e2e8f0' : '#1a1a2e',
  },

  menuItemBadge: {
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#ef4444',
    color: '#fff',
    fontSize: 11,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 6px',
  },

  divider: {
    height: 1,
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(255, 255, 255, 0.06)'
      : 'rgba(0, 0, 0, 0.06)',
    margin: '8px 12px',
  },

  // Profile Section
  profileSection: {
    borderTop: theme.palette.type === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.06)'
      : '1px solid rgba(0, 0, 0, 0.06)',
    padding: '16px',
    marginTop: 8,
  },

  profileHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    marginBottom: 16,
  },

  profileAvatar: {
    width: 48,
    height: 48,
    fontSize: 18,
    fontWeight: 600,
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(91, 124, 255, 0.2)'
      : 'rgba(91, 124, 255, 0.1)',
    color: '#5b7cff',
  },

  profileInfo: {
    flex: 1,
    minWidth: 0,
  },

  profileName: {
    fontSize: 15,
    fontWeight: 600,
    color: theme.palette.type === 'dark' ? '#f1f5f9' : '#1a1a2e',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  profileEmail: {
    fontSize: 12,
    color: theme.palette.type === 'dark' ? '#94a3b8' : '#64748b',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  profileMeta: {
    marginTop: 10,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  profileMetaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12,
    color: theme.palette.type === 'dark' ? '#cbd5e1' : '#475569',
    '& svg': {
      width: 14,
      height: 14,
      color: theme.palette.type === 'dark' ? '#7c9aff' : '#5b7cff',
    },
  },

  // Settings Row
  settingsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 0',
  },

  settingsLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.type === 'dark' ? '#e2e8f0' : '#1a1a2e',
  },

  settingsIcon: {
    width: 18,
    height: 18,
    color: theme.palette.type === 'dark' ? '#88898D' : '#9f9f9f',
  },

  // Language Pills
  languagePills: {
    display: 'flex',
    gap: 8,
    marginTop: 8,
  },

  languagePill: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 8px',
    borderRadius: 12,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: theme.palette.type === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.08)'
      : '1px solid rgba(0, 0, 0, 0.06)',
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)',
    '&:active': {
      transform: 'scale(0.95)',
    },
  },

  languagePillSelected: {
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(91, 124, 255, 0.15)'
      : 'rgba(91, 124, 255, 0.12)',
    borderColor: theme.palette.type === 'dark'
      ? 'rgba(91, 124, 255, 0.5)'
      : 'rgba(91, 124, 255, 0.4)',
  },

  languageFlag: {
    fontSize: 20,
    lineHeight: 1,
    marginBottom: 4,
  },

  languageCode: {
    fontSize: 11,
    fontWeight: 600,
    color: theme.palette.type === 'dark' ? '#94a3b8' : '#64748b',
  },

  languageCodeSelected: {
    color: theme.palette.type === 'dark' ? '#7c9aff' : '#5b7cff',
  },

  // Theme Toggle
  themeToggle: {
    width: 44,
    height: 44,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
    cursor: 'pointer',
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.04)',
    transition: 'all 0.2s ease',
    '&:active': {
      transform: 'scale(0.95)',
    },
  },

  themeIcon: {
    width: 22,
    height: 22,
    color: theme.palette.type === 'dark' ? '#fbbf24' : '#5b7cff',
  },

  // Volume Slider
  volumeSliderWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginTop: 8,
  },

  volumeIcon: {
    width: 20,
    height: 20,
    color: theme.palette.type === 'dark' ? '#94a3b8' : '#64748b',
    cursor: 'pointer',
  },

  volumeSlider: {
    flex: 1,
    color: '#5b7cff',
    '& .MuiSlider-track': {
      height: 4,
      borderRadius: 2,
    },
    '& .MuiSlider-rail': {
      height: 4,
      borderRadius: 2,
      backgroundColor: theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)',
    },
    '& .MuiSlider-thumb': {
      width: 18,
      height: 18,
      backgroundColor: '#fff',
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
    },
  },

  volumeValue: {
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.type === 'dark' ? '#7c9aff' : '#5b7cff',
    minWidth: 36,
    textAlign: 'right',
  },

  // Action Buttons
  actionButtons: {
    display: 'flex',
    gap: 10,
    marginTop: 16,
  },

  actionButton: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '14px',
    borderRadius: 14,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    transition: 'all 0.15s ease',
    '&:active': {
      transform: 'scale(0.98)',
    },
  },

  profileButton: {
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(91, 124, 255, 0.15)'
      : 'rgba(91, 124, 255, 0.1)',
    color: '#5b7cff',
  },

  logoutButton: {
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(239, 68, 68, 0.15)'
      : 'rgba(239, 68, 68, 0.1)',
    color: '#ef4444',
  },
}));

const BottomNavigation = ({
  user,
  volume,
  setVolume,
  toggleColorMode,
  onOpenProfile,
  onLogout,
  connectionWarning
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const location = useLocation();
  const history = useHistory();
  const { whatsApps } = useContext(WhatsAppsContext);
  const socketManager = useContext(SocketContext);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(
    localStorage.getItem('i18nextLng') || 'pt'
  );
  const [showLeads, setShowLeads] = useState(false);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [internalUnreadChannels, setInternalUnreadChannels] = useState(0);
  const { getPlanCompany } = usePlans();

  const isDark = theme.palette.type === 'dark';

  // Esconder bottomNav quando estiver dentro de uma conversa (como WhatsApp)
  const isInsideTicket = /^\/tickets\/[^/]+/.test(location.pathname);
  const isInsideInternalChat = /^\/internal-chat\/.+/.test(location.pathname);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const handleResize = () => {
      // Se a viewport visível for muito menor que a tela, o teclado está aberto
      const keyboardVisible = viewport.height < window.innerHeight * 0.75;
      setKeyboardOpen(keyboardVisible);
    };

    viewport.addEventListener('resize', handleResize);
    return () => viewport.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    async function fetchPlan() {
      if (!user?.companyId) return;
      const planConfigs = await getPlanCompany(undefined, user.companyId);
      setShowLeads(planConfigs?.plan?.useLeads !== false);
    }
    fetchPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const isInternalOnly = user?.accessScope === "internal";
  const isAttendanceOnly = user?.accessScope === "attendance";

  // Chat Interno — badge de canais com mensagens não lidas (mesmo padrão
  // do MainListItems). Backend emite em `user-${id}` com actions
  // newInternalMessage / internalChannelRead — debounce evita storm.
  useEffect(() => {
    if (!user?.companyId || !user?.id) return undefined;
    if (isAttendanceOnly) return undefined;

    const fetchInternalUnread = async () => {
      try {
        const { data } = await api.get("/internal-chat/unread-counts");
        setInternalUnreadChannels(Object.keys(data?.channels || {}).length);
      } catch (err) {
        // silenciar
      }
    };

    fetchInternalUnread();

    const socket = socketManager.getSocket(user.companyId);
    const userEvent = `user-${user.id}`;
    let debounceTimer;

    const handleInternalEvent = (data) => {
      if (!data) return;
      if (data.action !== "newInternalMessage" && data.action !== "internalChannelRead") return;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(fetchInternalUnread, 600);
    };

    socket.on(userEvent, handleInternalEvent);
    return () => {
      socket.off(userEvent, handleInternalEvent);
      clearTimeout(debounceTimer);
    };
  }, [socketManager, user?.companyId, user?.id, isAttendanceOnly]);

  const mainNavItems = [
    ...(!isInternalOnly ? [
      { path: '/tickets', icon: MessageCircle, label: i18n.t("mainDrawer.listItems.tickets") },
      { path: '/contacts', icon: UserRound, label: i18n.t("mainDrawer.listItems.contacts") },
    ] : []),
    { path: '/', icon: ChartNoAxesCombined, label: 'Dashboard' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleNavClick = (path) => {
    history.push(path);
  };

  const handleLanguageChange = async (langCode) => {
    setSelectedLanguage(langCode);
    changeLanguage(langCode);
    try {
      await api.post(`/users/set-language/${langCode}`);
    } catch (error) {
      console.error(error);
    }
  };

  const handleVolumeChange = (event, newValue) => {
    setVolume(newValue);
    localStorage.setItem("volume", newValue);
  };

  const handleMuteToggle = () => {
    const newVolume = volume === 0 ? 0.7 : 0;
    setVolume(newVolume);
    localStorage.setItem("volume", newVolume);
  };

  const getVolumeIcon = () => {
    if (volume === 0) return VolumeX;
    if (volume < 0.5) return Volume1;
    return Volume2;
  };

  const VolumeIcon = getVolumeIcon();

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getUserJobRoleLabel = (currentUser) => {
    return currentUser?.jobRole?.name
      || i18n.t("mainDrawer.appBar.noJobRole")
      || "Sem cargo";
  };

  const handleMenuItemClick = (path) => {
    history.push(path);
    setDrawerOpen(false);
  };

  return (
    <>
      {/* Bottom Navigation Bar — esconde dentro de conversa e quando teclado aberto */}
      <div className={classes.bottomNav} style={(keyboardOpen || isInsideTicket || isInsideInternalChat) ? { display: 'none' } : undefined}>
        {mainNavItems.map((item) => (
          <div
            key={item.path}
            className={`${classes.navItem} ${isActive(item.path) ? classes.navItemActive : ''}`}
            onClick={() => handleNavClick(item.path)}
          >
            <item.icon size={24} strokeWidth={1.5} className={classes.navIcon} />
            <span className={classes.navLabel}>{item.label}</span>
          </div>
        ))}
        <div
          className={`${classes.navItem} ${drawerOpen ? classes.navItemActive : ''}`}
          onClick={() => setDrawerOpen(true)}
        >
          <MoreHorizontal size={24} strokeWidth={1.5} className={classes.navIcon} />
          <span className={classes.navLabel}>Mais</span>
        </div>
      </div>

      {/* More Drawer */}
      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ className: classes.drawerPaper }}
      >
        <div className={classes.drawerHandle} />

        <div className={classes.drawerHeader}>
          <span className={classes.drawerTitle}>Menu</span>
          <div className={classes.closeButton} onClick={() => setDrawerOpen(false)}>
            <X size={20} />
          </div>
        </div>

        <div className={classes.drawerContent}>
          {/* Navigation Items */}
          <div className={classes.menuSection}>
            {/* Quick Messages */}
            {!isInternalOnly && (
              <div
                className={`${classes.menuItem} ${isActive('/quick-messages') ? classes.menuItemActive : ''}`}
                onClick={() => handleMenuItemClick('/quick-messages')}
              >
                <Pen size={22} strokeWidth={1.5} className={classes.menuItemIcon} />
                <span className={classes.menuItemLabel}>{i18n.t("mainDrawer.listItems.quickMessages")}</span>
              </div>
            )}

            {/* Tags */}
            {!isInternalOnly && (
              <div
                className={`${classes.menuItem} ${isActive('/tags') ? classes.menuItemActive : ''}`}
                onClick={() => handleMenuItemClick('/tags')}
              >
                <Tags size={22} strokeWidth={1.5} className={classes.menuItemIcon} />
                <span className={classes.menuItemLabel}>{i18n.t("mainDrawer.listItems.tags")}</span>
              </div>
            )}

            {/* Boards */}
            {showLeads && (
              <div
                className={`${classes.menuItem} ${isActive('/crm') ? classes.menuItemActive : ''}`}
                onClick={() => handleMenuItemClick('/crm')}
              >
                <SquareKanban size={22} strokeWidth={1.5} className={classes.menuItemIcon} />
                <span className={classes.menuItemLabel}>Boards</span>
              </div>
            )}

            {/* Chat Interno */}
            {!isAttendanceOnly && (
              <div
                className={`${classes.menuItem} ${isActive('/internal-chat') ? classes.menuItemActive : ''}`}
                onClick={() => handleMenuItemClick('/internal-chat')}
              >
                <MessagesSquare size={22} strokeWidth={1.5} className={classes.menuItemIcon} />
                <span className={classes.menuItemLabel}>Chat Interno</span>
                {internalUnreadChannels > 0 && (
                  <span className={classes.menuItemBadge}>
                    {internalUnreadChannels > 99 ? '99+' : internalUnreadChannels}
                  </span>
                )}
              </div>
            )}

          </div>

          {/* Admin Section */}
          <Can
            role={user?.profile}
            perform="drawer-admin-items:view"
            yes={() => (
              <>
                <div className={classes.divider} />
                <div className={classes.menuSection}>
                  <div className={classes.sectionTitle}>{i18n.t("mainDrawer.listItems.administration")}</div>

                  {/* Connections */}
                  {!isInternalOnly && (
                    <div
                      className={`${classes.menuItem} ${isActive('/connections') ? classes.menuItemActive : ''}`}
                      onClick={() => handleMenuItemClick('/connections')}
                    >
                      <Link size={22} strokeWidth={1.5} className={classes.menuItemIcon} />
                      <span className={classes.menuItemLabel}>{i18n.t("mainDrawer.listItems.connections")}</span>
                      {connectionWarning && <span className={classes.menuItemBadge}>!</span>}
                    </div>
                  )}

                  {/* Queues */}
                  {!isInternalOnly && (
                    <div
                      className={`${classes.menuItem} ${isActive('/queues') ? classes.menuItemActive : ''}`}
                      onClick={() => handleMenuItemClick('/queues')}
                    >
                      <Workflow size={22} strokeWidth={1.5} className={classes.menuItemIcon} />
                      <span className={classes.menuItemLabel}>{i18n.t("mainDrawer.listItems.queues")}</span>
                    </div>
                  )}

                  {/* Users */}
                  <div
                    className={`${classes.menuItem} ${isActive('/users') ? classes.menuItemActive : ''}`}
                    onClick={() => handleMenuItemClick('/users')}
                  >
                    <UsersRound size={22} strokeWidth={1.5} className={classes.menuItemIcon} />
                    <span className={classes.menuItemLabel}>{i18n.t("mainDrawer.listItems.users")}</span>
                  </div>

                  {/* Fluxo */}
                  {!isInternalOnly && (
                    <div
                      className={`${classes.menuItem} ${isActive('/fluxo') ? classes.menuItemActive : ''}`}
                      onClick={() => handleMenuItemClick('/fluxo')}
                    >
                      <GitBranch size={22} strokeWidth={1.5} className={classes.menuItemIcon} />
                      <span className={classes.menuItemLabel}>Fluxo</span>
                    </div>
                  )}

                  {/* Meta Templates */}
                  {!isInternalOnly && (
                    <div
                      className={`${classes.menuItem} ${isActive('/meta-templates') ? classes.menuItemActive : ''}`}
                      onClick={() => handleMenuItemClick('/meta-templates')}
                    >
                      <FileText size={22} strokeWidth={1.5} className={classes.menuItemIcon} />
                      <span className={classes.menuItemLabel}>{i18n.t("mainDrawer.listItems.metaTemplates")}</span>
                    </div>
                  )}

                  {/* Settings */}
                  <div
                    className={`${classes.menuItem} ${isActive('/settings') ? classes.menuItemActive : ''}`}
                    onClick={() => handleMenuItemClick('/settings')}
                  >
                    <Settings size={22} strokeWidth={1.5} className={classes.menuItemIcon} />
                    <span className={classes.menuItemLabel}>{i18n.t("mainDrawer.listItems.settings")}</span>
                  </div>

                  {/* Super Dashboard */}
                  {user?.super && (
                    <div
                      className={`${classes.menuItem} ${isActive('/super-dashboard') ? classes.menuItemActive : ''}`}
                      onClick={() => handleMenuItemClick('/super-dashboard')}
                    >
                      <LayoutDashboard size={22} strokeWidth={1.5} className={classes.menuItemIcon} />
                      <span className={classes.menuItemLabel}>Super Dashboard</span>
                    </div>
                  )}

                  {/* Super Admin Panel */}
                  {user?.super && (
                    <div
                      className={`${classes.menuItem} ${isActive('/super-admin-panel') ? classes.menuItemActive : ''}`}
                      onClick={() => handleMenuItemClick('/super-admin-panel')}
                    >
                      <Building2 size={22} strokeWidth={1.5} className={classes.menuItemIcon} />
                      <span className={classes.menuItemLabel}>Admin Panel</span>
                    </div>
                  )}

                  {/* Gerenciar Novidades */}
                  {user?.super && (
                    <div
                      className={`${classes.menuItem} ${isActive('/news-posts') ? classes.menuItemActive : ''}`}
                      onClick={() => handleMenuItemClick('/news-posts')}
                    >
                      <Newspaper size={22} strokeWidth={1.5} className={classes.menuItemIcon} />
                      <span className={classes.menuItemLabel}>Gerenciar Novidades</span>
                    </div>
                  )}
                </div>
              </>
            )}
          />

          {/* Profile Section */}
          <div className={classes.profileSection}>
            <div className={classes.profileHeader}>
              {user?.profileImage ? (
                <Avatar src={user.profileImage} className={classes.profileAvatar} />
              ) : (
                <Avatar className={classes.profileAvatar}>
                  {getInitials(user?.name)}
                </Avatar>
              )}
              <div className={classes.profileInfo}>
                <div className={classes.profileName}>{user?.name || 'Usuário'}</div>
                <div className={classes.profileEmail}>{user?.email || ''}</div>
                <div className={classes.profileMeta}>
                  <div className={classes.profileMetaItem}>
                    <Building2 />
                    <span>{user?.company?.name || 'Empresa'}</span>
                  </div>
                  <div className={classes.profileMetaItem}>
                    <Briefcase />
                    <span>{getUserJobRoleLabel(user)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Language */}
            <div className={classes.settingsRow}>
              <span className={classes.settingsLabel}>
                <Globe className={classes.settingsIcon} />
                {i18n.t("mainDrawer.appBar.language") || "Idioma"}
              </span>
            </div>
            <div className={classes.languagePills}>
              {languages.map((lang) => (
                <div
                  key={lang.code}
                  className={`${classes.languagePill} ${
                    selectedLanguage === lang.code ? classes.languagePillSelected : ''
                  }`}
                  onClick={() => handleLanguageChange(lang.code)}
                >
                  <span className={classes.languageFlag}>{lang.flag}</span>
                  <span className={`${classes.languageCode} ${
                    selectedLanguage === lang.code ? classes.languageCodeSelected : ''
                  }`}>
                    {lang.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Theme */}
            <div className={classes.settingsRow} style={{ marginTop: 16 }}>
              <span className={classes.settingsLabel}>
                {isDark ? <Moon className={classes.settingsIcon} /> : <Sun className={classes.settingsIcon} />}
                {i18n.t("mainDrawer.appBar.appearance") || "Aparência"}
              </span>
              <div className={classes.themeToggle} onClick={toggleColorMode}>
                {isDark ? (
                  <Sun className={classes.themeIcon} />
                ) : (
                  <Moon className={classes.themeIcon} />
                )}
              </div>
            </div>

            {/* Volume */}
            <div className={classes.settingsRow} style={{ marginTop: 8 }}>
              <span className={classes.settingsLabel}>
                <Volume2 className={classes.settingsIcon} />
                {i18n.t("mainDrawer.appBar.notificationVolume") || "Som"}
              </span>
              <span className={classes.volumeValue}>{Math.round(volume * 100)}%</span>
            </div>
            <div className={classes.volumeSliderWrapper}>
              <VolumeIcon
                className={classes.volumeIcon}
                onClick={handleMuteToggle}
              />
              <Slider
                className={classes.volumeSlider}
                value={volume}
                min={0}
                max={1}
                step={0.05}
                onChange={handleVolumeChange}
              />
            </div>

            {/* Action Buttons */}
            <div className={classes.actionButtons}>
              <div
                className={`${classes.actionButton} ${classes.profileButton}`}
                onClick={() => { onOpenProfile(); setDrawerOpen(false); }}
              >
                <UserRound size={18} />
                {i18n.t("mainDrawer.appBar.user.profile") || "Perfil"}
              </div>
              <div
                className={`${classes.actionButton} ${classes.logoutButton}`}
                onClick={() => { onLogout(); setDrawerOpen(false); }}
              >
                <LogOut size={18} />
                {i18n.t("mainDrawer.appBar.user.logout") || "Sair"}
              </div>
            </div>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default BottomNavigation;
